import { ApeironCloseButton, TextInput } from '@apeiron/temp'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material'
import {
  ChangeEvent,
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'

const StyledCloseButton = styled(ApeironCloseButton)`
  position: absolute;
  width: 32px;
  height: 32px;
  top: 20px;
  right: 20px;
  ${props => props.theme.breakpoints.down('res480')} {
    top: 12px;
    right: 12px;
  }
`

const AddDialogPaper = styled(Paper)`
  border-radius: 5px;
  background-color: #262829;
  width: 100%;
  max-width: 600px;
  color: #ffffff;

  ${props => props.theme.breakpoints.down('res480')} {
    margin: 18px;
  }
`

const AddDialogTitle = styled(DialogTitle)`
  font-weight: 400;
  font-size: 26px;
  line-height: 52px;
  letter-spacing: -0.015em;
  text-align: center;

  padding-top: 42px;
  padding-bottom: 0;

  ${props => props.theme.breakpoints.down('res480')} {
    padding-top: 51px;
    font-size: 26px;
    line-height: 30px;
  }
`

const AddDialogSubtitle = styled('p')`
  font-weight: 400;
  font-size: 16px;
  text-align: center;
  line-height: 30px;
  letter-spacing: -0.015em;

  color: #838383;
  margin: 0;
  margin-top: -9px;
  margin-bottom: 21px;

  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 14px;
    line-height: 18px;
    margin-top: 11px;
    margin-bottom: 12px;
  }
`

const AddDialogField = styled(Box)`
  height: 45px;
  display: flex;
  align-items: stretch;

  margin: 0 36px;
  margin-bottom: 24px;

  ${props => props.theme.breakpoints.down('res480')} {
    height: 28px;

    margin: 0 -4px;
    margin-bottom: 24px;
  }
`

const AddDialogNameLabel = styled('label')`
  display: none;
  font-size: 14px;
  line-height: 28px;
  margin-right: 12px;

  ${props => props.theme.breakpoints.down('res480')} {
    display: block;
    margin-right: 10px;
  }
`

const AddDialogNameInput = styled(TextInput)`
  margin-right: 12px;
  flex: 1;

  & .MuiOutlinedInput-input {
    font-size: 16px;
    line-height: 20px;

    padding-left: 12px;
  }

  ${props => props.theme.breakpoints.down('res480')} {
    margin-right: 10px;
    &.MuiOutlinedInput-root,
    &.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
      border-radius: 5px;
    }
  }
`

const AddDialogSaveButton = styled(Button)`
  font-weight: 400;
  font-size: 16px;
  line-height: 18px;
  letter-spacing: -0.015em;

  width: 100px;
  text-transform: none;
  border-radius: 2px;

  ${props => props.theme.breakpoints.down('res480')} {
    width: 90px;
    border-radius: 5px;
  }
`

const SaveFilterDialog: FC<Props> = (props: Props) => {
  const { className, isOpened, setIsOpened, onAdd } = props

  const { t } = useTranslation()

  const theme = useTheme()

  const matches = useMediaQuery(theme.breakpoints.up('res480'))

  const inputRef = useRef<HTMLInputElement | null>(null)

  const [name, setName] = useState('')

  const onCloseDialog = useCallback(() => {
    setIsOpened(false)
  }, [setIsOpened])

  const onAddSearch = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (name === '') {
        // TODO: validation

        Promise.resolve().then(() => {
          inputRef?.current?.focus()
        })

        return
      }

      onAdd?.(name)
      setIsOpened(false)
    },
    [onAdd, name, setIsOpened]
  )

  const onNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setName(e.currentTarget.value)
  }, [])

  useEffect(() => {
    if (isOpened) {
      setName('')
    }
  }, [isOpened])

  return (
    <Dialog
      className={className}
      PaperComponent={AddDialogPaper}
      open={isOpened}
      onClose={onCloseDialog}
    >
      <form onSubmit={onAddSearch}>
        <AddDialogTitle>
          {t('filter.sections.saved_search.add.title')}
        </AddDialogTitle>
        <DialogContent>
          <AddDialogSubtitle>
            {t('filter.sections.saved_search.add.subtitle')}
          </AddDialogSubtitle>
          <AddDialogField>
            <AddDialogNameLabel>
              {t('filter.sections.saved_search.add.label')}
            </AddDialogNameLabel>
            <AddDialogNameInput
              inputRef={inputRef}
              autoFocus
              placeholder={
                matches ? t('filter.sections.saved_search.add.placeholder') : ''
              }
              value={name}
              onChange={onNameChange}
            />
            <AddDialogSaveButton
              type='submit'
              color='primary'
              variant='contained'
            >
              {t('filter.sections.saved_search.add.save')}
            </AddDialogSaveButton>
          </AddDialogField>
        </DialogContent>
        <StyledCloseButton onClick={onCloseDialog} />
      </form>
    </Dialog>
  )
}

type Props = {
  className?: string
  isOpened: boolean
  setIsOpened: (isOpened: boolean) => void
  onAdd?: (name: string) => void
}

export default SaveFilterDialog
